import { useState, useEffect } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';

interface Permission {
  id: string;
  name: string;
  /** Human-friendly display name (from the backend catalog); falls back to the key. */
  label?: string | null;
  /** One-line explanation shown as a tooltip. */
  description?: string | null;
  category?: string | null;
  [key: string]: unknown;
}

interface PermissionMatrixProps {
  /** Either an array of permissions, or `{ permissions: [...] }`. */
  allPermissions: Permission[] | { permissions?: Permission[] } | null | undefined;
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  /** Read-only mode (e.g. viewing a system role) — checkboxes/toggles disabled. */
  disabled?: boolean;
}

/** Fallback label when the backend catalog has no `label` (e.g. legacy rows). */
function fallbackLabel(name: string): string {
  const action = name.split('.')[1] || name;
  return action.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Grid-based permission matrix for assigning permissions to a role. Categories are
 * derived from the permission data (category field, else the `name.split('.')[0]`
 * prefix) — so it adapts to whatever permission set each node's backend returns.
 */
export default function PermissionMatrix({ allPermissions, selectedIds, onChange, disabled = false }: PermissionMatrixProps) {
  const [grouped, setGrouped] = useState<Record<string, Permission[]>>({});

  useEffect(() => {
    if (!allPermissions) return;
    const groups: Record<string, Permission[]> = {};
    const permList = Array.isArray(allPermissions) ? allPermissions : allPermissions.permissions;
    if (!Array.isArray(permList)) return;
    for (const perm of permList) {
      const cat = perm.category || perm.name.split('.')[0];
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(perm);
    }
    setGrouped(groups);
  }, [allPermissions]);

  function togglePermission(permId: string) {
    if (disabled) return;
    const next = selectedIds.includes(permId)
      ? selectedIds.filter((id) => id !== permId)
      : [...selectedIds, permId];
    onChange(next);
  }

  function toggleCategory(category: string) {
    if (disabled) return;
    const catIds = (grouped[category] || []).map((p) => p.id);
    const allSelected = catIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      onChange(selectedIds.filter((id) => !catIds.includes(id)));
    } else {
      onChange([...new Set([...selectedIds, ...catIds])]);
    }
  }

  function selectAll() {
    onChange(Object.values(grouped).flat().map((p) => p.id));
  }

  function deselectAll() {
    onChange([]);
  }

  const categories = Object.keys(grouped).sort();

  if (categories.length === 0) {
    return <p className="text-[13px] text-muted-foreground py-4">Loading permissions…</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <span className="text-[12px] text-muted-foreground">
          {selectedIds.length} of {Object.values(grouped).flat().length} selected
        </span>
        {!disabled && (
          <>
            <button type="button" onClick={selectAll} className="text-[11px] font-medium text-brand hover:underline">
              Select All
            </button>
            <button type="button" onClick={deselectAll} className="text-[11px] font-medium text-destructive hover:underline">
              Clear All
            </button>
          </>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {categories.map((category) => {
          const catPerms = grouped[category];
          const catIds = catPerms.map((p) => p.id);
          const allSelected = catIds.every((id) => selectedIds.includes(id));
          const someSelected = catIds.some((id) => selectedIds.includes(id));

          return (
            <div key={category} className="rounded-lg border border-border bg-card p-3">
              <div className="flex items-center justify-between mb-2">
                <button
                  type="button"
                  onClick={() => toggleCategory(category)}
                  className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-wider text-foreground hover:text-brand transition-colors"
                >
                  <span
                    className={`flex h-4 w-4 items-center justify-center rounded border text-[10px] transition-colors ${
                      allSelected
                        ? 'bg-brand border-brand text-white'
                        : someSelected
                        ? 'bg-brand/20 border-brand text-brand'
                        : 'border-border'
                    }`}
                  >
                    {allSelected && <CheckIcon className="h-3 w-3" />}
                    {someSelected && !allSelected && <span className="block h-1.5 w-1.5 rounded-sm bg-brand" />}
                  </span>
                  {category}
                </button>
                <span className="text-[10px] text-muted-foreground">
                  {catIds.filter((id) => selectedIds.includes(id)).length}/{catIds.length}
                </span>
              </div>

              <div className="space-y-1">
                {catPerms.map((perm) => (
                  <label
                    key={perm.id}
                    title={perm.description || perm.name}
                    className={`flex items-start gap-2 rounded-md px-2 py-1 transition-colors ${
                      disabled ? 'cursor-default opacity-80' : 'cursor-pointer hover:bg-muted'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(perm.id)}
                      onChange={() => togglePermission(perm.id)}
                      disabled={disabled}
                      className="mt-0.5 h-3.5 w-3.5 rounded border-border bg-input text-brand accent-brand disabled:opacity-60"
                    />
                    <span className="flex flex-col">
                      <span className="text-[12px] leading-tight text-foreground">
                        {perm.label || fallbackLabel(perm.name)}
                      </span>
                      {perm.description && (
                        <span className="text-[10px] leading-tight text-muted-foreground">{perm.description}</span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
